import { Client } from '@botpress/client'
import { transforms } from '@botpress/sdk'

import { auth } from '../auth/index.js'
import { AgentProject } from '../agent-project/agent-project.js'
import {
  LocalTable,
  RemoteTable,
  TableSyncPlan,
  TableSyncResult,
  TableSyncItem,
  TableSyncOperation,
  TableSyncOptions,
} from './types.js'

export interface TableManagerOptions {
  project: AgentProject
  botId?: string
}

export class TableManager {
  private client?: Client
  private botId?: string
  private project: AgentProject

  constructor(options: TableManagerOptions) {
    this.botId = options.botId
    this.project = options.project
  }

  private async getClient(): Promise<Client> {
    if (!this.client) {
      const credentials = await auth.getActiveCredentials()

      this.assertBotId('initialize client')

      this.client = new Client({
        token: credentials.token,
        apiUrl: credentials.apiUrl,
        botId: this.botId,
        headers: {
          'x-multiple-integrations': 'true',
        },
      })
    }
    return this.client
  }

  private assertBotId(operation: string): void {
    if (!this.botId) {
      throw new Error(
        `Operation "${operation}" requires a bot ID. ` +
          'Please deploy your agent first or create agent.json with botId and workspaceId.'
      )
    }
  }

  async getLocalTables(): Promise<LocalTable[]> {
    const tables: LocalTable[] = []

    for (const tableRef of this.project.tables) {
      try {
        // Load the actual table module to get the full definition

        tables.push({
          name: tableRef.definition.name,
          factor: tableRef.definition.factor,
          schema: tableRef.definition.schema,
        })
      } catch (error) {}
    }

    return tables
  }

  async getRemoteTables(): Promise<RemoteTable[]> {
    this.assertBotId('get remote tables')

    const client = await this.getClient()

    try {
      // List tables from Botpress API
      const response = await client.listTables({})

      return response.tables.map((table) => ({
        id: table.id,
        name: table.name,
        createdAt: table.createdAt || new Date().toISOString(),
        updatedAt: table.updatedAt || new Date().toISOString(),
        schema: table.schema,
      }))
    } catch (error) {
      console.error('Failed to list remote tables:', error)
      return []
    }
  }

  async createSyncPlan(): Promise<TableSyncPlan> {
    const localTables = await this.getLocalTables()
    const remoteTables = await this.getRemoteTables()

    const items: TableSyncItem[] = []
    const remoteMap = new Map(remoteTables.map((t) => [t.name, t]))

    // Check each local table
    for (const local of localTables) {
      const remote = remoteMap.get(local.name)

      if (!remote) {
        // Table doesn't exist remotely - need to create
        items.push({
          operation: TableSyncOperation.Create,
          localTable: local,
          reason: 'Table does not exist remotely',
        })
      } else {
        // Table exists - check if schema matches
        try {
          const localSchema = transforms.fromJSONSchema(local.schema)
          const remoteSchema = transforms.fromJSONSchema(remote.schema)

          if (!localSchema.isEqual(remoteSchema)) {
            // Schema has changed - need to update
            items.push({
              operation: TableSyncOperation.Update,
              localTable: local,
              remoteTable: remote,
              reason: 'Table schema has changed',
            })
          } else {
            // Schema matches - no update needed
            items.push({
              operation: TableSyncOperation.None,
              localTable: local,
              remoteTable: remote,
              reason: 'Table schema is up to date',
            })
          }
        } catch (error) {
          // If we can't compare schemas, assume they need updating
          items.push({
            operation: TableSyncOperation.Update,
            localTable: local,
            remoteTable: remote,
            reason: 'Unable to compare schemas - assuming update needed',
          })
        }

        // Remove from map so we can find orphaned tables
        remoteMap.delete(local.name)
      }
    }

    // Remaining remote tables don't have local counterparts
    for (const [, remote] of remoteMap) {
      items.push({
        operation: TableSyncOperation.Delete,
        remoteTable: remote,
        reason: 'Table no longer defined locally',
      })
    }

    // Calculate totals
    const totalCreate = items.filter((i) => i.operation === TableSyncOperation.Create).length
    const totalUpdate = items.filter((i) => i.operation === TableSyncOperation.Update).length
    const totalDelete = items.filter((i) => i.operation === TableSyncOperation.Delete).length
    const hasChanges = totalCreate > 0 || totalUpdate > 0 || totalDelete > 0

    return {
      items,
      totalCreate,
      totalUpdate,
      totalDelete,
      hasChanges,
    }
  }

  async executeSync(plan: TableSyncPlan, options: TableSyncOptions = {}): Promise<TableSyncResult> {
    this.assertBotId('sync tables')

    if (options.dryRun) {
      return {
        applied: false,
        success: [],
        failed: [],
        summary: {
          created: 0,
          updated: 0,
          deleted: 0,
          failed: 0,
        },
      }
    }

    const client = await this.getClient()
    const success: TableSyncItem[] = []
    const failed: Array<{ item: TableSyncItem; error: Error }> = []

    for (const item of plan.items) {
      if (item.operation === TableSyncOperation.None) {
        success.push(item)
        continue
      }

      try {
        switch (item.operation) {
          case TableSyncOperation.Create:
            if (item.localTable) {
              await client.createTable({
                name: item.localTable.name,
                factor: item.localTable.factor || 1,
                schema: item.localTable.schema,
                isComputeEnabled: true,
              })
              success.push(item)
            }
            break

          case TableSyncOperation.Update:
            if (item.localTable && item.remoteTable) {
              // Update table schema
              await client.updateTable({
                table: item.localTable.name,
                schema: item.localTable.schema,
              })
              success.push(item)
            }
            break

          case TableSyncOperation.Delete:
            if (item.remoteTable) {
              // Require explicit confirmation for deletion
              if (!options.autoConfirm) {
                console.warn(`Skipping deletion of table "${item.remoteTable.name}" - requires confirmation`)
                continue
              }
              await client.deleteTable({ table: item.remoteTable.name })
              success.push(item)
            }
            break
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        failed.push({ item, error: err })

        if (options.bailOnFailure) {
          break
        }
      }
    }

    return {
      applied: true,
      success,
      failed,
      summary: {
        created: success.filter((i) => i.operation === TableSyncOperation.Create).length,
        updated: success.filter((i) => i.operation === TableSyncOperation.Update).length,
        deleted: success.filter((i) => i.operation === TableSyncOperation.Delete).length,
        failed: failed.length,
      },
    }
  }
}
